import { useState } from 'react';
import {
    Box,
    Avatar,
    Typography,
    Collapse,
    Button,
    CircularProgress,
} from '@mui/material';
import { ChatBubbleOutline, Download } from '@mui/icons-material';
import moment from 'moment';
import { Paper, styled } from '@mui/material';
import { ITaskHistory } from '../../models/periodic/taskHistory';
import { getStatusConfig } from './PeriodicStatsOverview';
import { TFunction } from "i18next";
import { FileActionCard } from '../FileActionCard';

import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { generateFriendlyFilename } from '../../utils/download/fileUtils';

const StyledHistoryCard = styled(Paper)<{ statuscolor: string }>(({ theme, statuscolor }) => ({
    padding: 0,
    marginBottom: theme.spacing(3),
    border: `1px solid ${statuscolor}40`,
    borderRadius: 12,
    overflow: 'visible',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 6,
        backgroundColor: statuscolor,
        borderRadius: '12px 0 0 12px',
    },
}));

interface HistoryCardProps {
    item: ITaskHistory;
    isExpanded: boolean;
    t: TFunction;
    onImageClick: (url: string) => void;
    onDownload: (url: string) => void;
}

export const HistoryCard = ({ item, isExpanded, t, onImageClick, onDownload }: HistoryCardProps) => {
    const cfg = getStatusConfig(item.status, t);
    const actualContentExists = !!item.comment || (item.pictureUrls && item.pictureUrls.length > 0);
    const isVisible = actualContentExists && isExpanded;
    const hasImages = item.pictureUrls && item.pictureUrls.length > 0;

    const [isZipping, setIsZipping] = useState(false);

    const onDownloadZip = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!item.pictureUrls || item.pictureUrls.length === 0) return;

        try {
            setIsZipping(true);
            const zip = new JSZip();

            const promises = item.pictureUrls.map(async (url, index) => {
                const response = await fetch(url);
                const blob = await response.blob();

                const filename = generateFriendlyFilename(url, item.userName, item.actionDate.toString(), index);
                zip.file(filename, blob);
            });

            await Promise.all(promises);
            const content = await zip.generateAsync({ type: "blob" });

            const zipName = `${item.userName?.replace(/[^a-z0-9]/gi, '_')}_${moment(item.actionDate).format('YYYY-MM-DD')}.zip`;
            saveAs(content, zipName);

        } catch (error) {
        } finally {
            setIsZipping(false);
        }
    };

    return (
        <StyledHistoryCard statuscolor={cfg.color} elevation={0} sx={{ mb: 2 }}>
            {hasImages && isVisible && (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={isZipping ? <CircularProgress size={14} color="inherit" /> : <Download />}
                    onClick={onDownloadZip}
                    disabled={isZipping}
                    sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        zIndex: 10,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        borderRadius: '20px',
                        fontWeight: 700,
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        bgcolor: 'white',
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.light',
                        '&:hover': { bgcolor: '#f8f9fa' }
                    }}
                >
                    {isZipping ? t('processing') : t('downloadAll')}
                </Button>
            )}
            <Box sx={{
                p: 2,
                bgcolor: `${cfg.color}08`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: isVisible ? `1px solid ${cfg.color}20` : 'none',
                transition: 'border-bottom 0.2s ease-in-out',
            }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: cfg.color, fontWeight: 700, width: 32, height: 32, fontSize: '0.85rem' }}>
                        {item.userName?.substring(0, 1)}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {item.userName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {cfg.icon} {cfg.label}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {moment.utc(item.actionDate).local().format('LLL')}
                    </Typography>
                </Box>
            </Box>

            <Collapse in={isVisible}>
                <Box sx={{ p: 3 }}>
                    {item.comment && (
                        <Box sx={{ mb: item.pictureUrls?.length ? 3 : 0 }}>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                                <ChatBubbleOutline fontSize="small" /> {t('validation.comment')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6 }}>
                                {item.comment}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {item.pictureUrls?.map((url: string, i: number) => (
                            <FileActionCard
                                key={i}
                                url={url}
                                t={t}
                                onView={() => onImageClick(url)}
                                onDownload={onDownload}
                            />
                        ))}
                    </Box>
                </Box>
            </Collapse>
        </StyledHistoryCard>
    );
};